import React from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useSignIn } from "../../generated/hook";
import { useInvalidateConnectedUser } from "../../hooks/useInvalidateConnectedUser";
import Cookies from "js-cookie";
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  Snackbar,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { SignInInboundDto } from "../../generated/model";

// Type étendu temporaire en attendant la mise à jour d'Orval
interface ExtendedLoginRequest extends SignInInboundDto {
  rememberMe?: boolean;
}

const SignInPage: React.FC = () => {
  const navigate = useNavigate();
  const invalidateConnectedUser = useInvalidateConnectedUser();
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ExtendedLoginRequest>({
    defaultValues: {
      email: "",
      password: "",
      rememberMe: false,
    },
  });

  const {
    mutate: login,
    isError,
    isPending,
  } = useSignIn({
    mutation: {
      onSuccess: (data, variables) => {
        if (!data.token) {
          throw new Error("No token received");
        }

        // Déterminer la durée d'expiration du cookie en fonction de rememberMe
        const rememberMe = (variables.data as ExtendedLoginRequest).rememberMe;
        const cookieExpiration = rememberMe ? 14 : 7; // 14 jours si "Se souvenir de moi", sinon 7 jours

        // Store JWT in cookies
        Cookies.set("token", data.token, { expires: cookieExpiration });

        // Invalidate connected user query to refetch user data
        invalidateConnectedUser();

        setSnackbarOpen(true);
        setTimeout(() => {
          navigate("/");
        }, 1500);
      },
    },
  });

  const onSubmit = async (data: ExtendedLoginRequest) => {
    // Convertir vers LoginRequest pour l'API en gardant seulement les champs requis
    const loginData = {
      email: data.email,
      password: data.password,
      rememberMe: !!data.rememberMe,
    };
    login({ data: loginData });
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography component="h1" variant="h4" align="center" gutterBottom>
            Sign In
          </Typography>

          {isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              An error occurred
            </Alert>
          )}

          <form onSubmit={handleSubmit(onSubmit)}>
            <TextField
              {...register("email")}
              margin="normal"
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              error={!!errors.email}
              helperText={errors.email?.message}
            />
            <TextField
              {...register("password")}
              margin="normal"
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              error={!!errors.password}
              helperText={errors.password?.message}
            />
            <FormControlLabel
              control={
                <Checkbox
                  {...register("rememberMe")}
                  name="rememberMe"
                  color="primary"
                />
              }
              label="Se souvenir de moi (2 semaines)"
              sx={{ mt: 1, mb: 1 }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={isPending}
              startIcon={isPending ? <CircularProgress size={20} /> : undefined}
            >
              Sign In
            </Button>
          </form>
        </Paper>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        message="Successfully logged in!"
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      />
    </Container>
  );
};

export default SignInPage;
